import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Mail, Lock, Loader2, Sparkles, ArrowRight, Phone } from 'lucide-react'
import { login as loginApi } from '@/service/api/auth'
import { cn, hashPassword } from '@/lib/utils'
import { useUserStore } from '@/store/user'
import { useConfigStore } from '@/store/config'
import { CaptchaGate } from '@/lib/captcha/CaptchaGate'
import { toast } from 'sonner'
import {
    authCard,
    authFormInput,
    authIconBox,
    authInputIcon,
    authLink,
    authSubmitBtn,
} from '@/lib/ui-classes'

const getSchema = () => {
    return z.object({
        username: z.string().refine((val) => {
            const config = useConfigStore.getState().config
            const isEmail = config?.is_email ?? true
            const isPhone = config?.is_phone ?? false

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            const phoneRegex = /^1[3-9]\d{9}$/

            return emailRegex.test(val) || phoneRegex.test(val) // 由于历史注册原因，登录时支持手机号和邮箱，无论是否开启手机号注册和邮箱注册

            if (isEmail && isPhone) return emailRegex.test(val) || phoneRegex.test(val)
            if (isPhone) return phoneRegex.test(val)
            return emailRegex.test(val)
        }, { message: "请输入有效的账号格式" }),
        password: z.string().min(1, { message: "请输入密码" }),
    })
}

export default function Login() {
    const router = useRouter()
    const [loading, setLoading] = useState(false)
    const captchaTriggerRef = useRef<HTMLButtonElement | null>(null)
    const login = useUserStore((state) => state.login)
    const config = useConfigStore((state) => state.config)

    const isEmail = config?.is_email ?? true
    const isPhone = config?.is_phone ?? false
    const label = isEmail && isPhone ? '邮箱 / 手机号' : (isPhone ? '手机号' : '邮箱地址')
    const placeholder = isEmail && isPhone ? '请输入邮箱或手机号' : (isPhone ? '请输入手机号' : '请输入邮箱地址')
    const InputIcon = isPhone && !isEmail ? Phone : Mail

    const loginSchema = getSchema()
    const { register, handleSubmit, setValue, trigger, formState: { errors } } = useForm<z.infer<typeof loginSchema>>({
        resolver: zodResolver(loginSchema),
    })

    useEffect(() => {
        const savedUsername = localStorage.getItem('login_username')
        if (savedUsername) {
            setValue('username', savedUsername)
        }
    }, [setValue])

    const performLogin = async (data: z.infer<typeof loginSchema>, cap_token?: string) => {
        setLoading(true)
        try {
            const resData = await loginApi({
                ...data,
                password: hashPassword(data.password),
                cap_token,
            })
            localStorage.setItem('login_username', data.username)
            login(resData.token, resData.user)
            router.push('/projects')
        } catch (error) {
            // Error handled by request helper
        } finally {
            setLoading(false)
        }
    }

    const onSubmit = async (data: z.infer<typeof loginSchema>) => {
        await performLogin(data)
    }

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (loading) return
        captchaTriggerRef.current?.click()
    }

    const systemConfig = useConfigStore(state => state.config)
    const siteLogo = systemConfig?.site_logo || ''

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-black relative overflow-hidden">
            <div className="absolute inset-0 w-full h-full">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#07F4F2]/15 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#C1FAB8]/10 rounded-full blur-[120px]" />
                <div className="absolute inset-0 opacity-20 brightness-100 contrast-150 mix-blend-overlay" />
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />
            </div>

            <div className="relative z-10 w-full max-w-md p-8 mx-4">
                <div className="flex flex-col items-center mb-8 text-center">
                    {siteLogo ? (
                        <div className="mb-4 flex w-full max-w-xs justify-center px-2">
                            <img
                                src={siteLogo}
                                alt="Site Logo"
                                className="h-16 w-auto max-h-16 max-w-full object-contain rounded-xl"
                            />
                        </div>
                    ) : (
                        <div className={cn(authIconBox, 'h-16 w-16 mb-4')}>
                            <Sparkles className="h-6 w-6 text-[#0a0a0a]" />
                        </div>
                    )}
                    <h1 className="text-3xl font-bold tracking-tight text-white mb-2">欢迎回来</h1>
                    <p className="text-zinc-400">登录您的账户，继续您的创作之旅</p>
                </div>

                <div className={authCard}>
                    <form onSubmit={handleFormSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="username" className="text-zinc-300">{label}</Label>
                            <div className="relative group">
                                <InputIcon className={cn('absolute left-3 top-3 h-4 w-4', authInputIcon)} />
                                <Input
                                    id="username"
                                    placeholder={placeholder}
                                    type={isPhone && !isEmail ? "tel" : "text"}
                                    disabled={loading}
                                    {...register('username')}
                                    className={authFormInput}
                                />
                            </div>
                            {errors.username && (
                                <p className="text-xs text-red-500 mt-1">{errors.username.message}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="password" className="text-zinc-300">密码</Label>
                                <Link href="/auth/forgot-password" className={cn('text-sm', authLink)}>
                                    忘记密码?
                                </Link>
                            </div>
                            <div className="relative group">
                                <Lock className={cn('absolute left-3 top-3 h-4 w-4', authInputIcon)} />
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="请输入密码"
                                    disabled={loading}
                                    {...register('password')}
                                    className={authFormInput}
                                />
                            </div>
                            {errors.password && (
                                <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>
                            )}
                        </div>

                        <CaptchaGate
                            scene="login"
                            mode="floating"
                            floatingPosition="bottom"
                            onBeforeFloating={async () => await trigger()}
                            onSolved={(token) => {
                                void handleSubmit((data) => performLogin(data, token))()
                            }}
                            onBypass={() => {
                                void handleSubmit(onSubmit)()
                            }}
                            onError={(message: string) => toast.error(message || '人机验证失败，请重试')}
                            disabled={loading}
                            trigger={
                                <button
                                    type="button"
                                    ref={captchaTriggerRef}
                                    disabled={loading}
                                    className={authSubmitBtn}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            登录中...
                                        </>
                                    ) : (
                                        <>
                                            登录
                                            <ArrowRight className="h-4 w-4" />
                                        </>
                                    )}
                                </button>
                            }
                        />
                        <button type="submit" tabIndex={-1} aria-hidden="true" className="hidden" />
                    </form>

                    {(isEmail || isPhone) && (
                        <div className="mt-6 text-center text-sm text-zinc-400">
                            还没有账户?{' '}
                            <Link href="/auth/register" className={authLink}>
                                立即注册
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
